import { useEffect, useRef, useState } from 'react';
import type { PlayerView } from '@il-colpo/engine';
import { audio } from '../audio/manager.ts';
import { sceneForPhase, type Scene } from '../audio/scenes.ts';
import { sfxForEvent, type SfxKey } from '../audio/sfx.ts';

/** At most this many effects for the events of one update: a burst is one moment, not a storm. */
const MAX_PER_UPDATE = 3;

/** The effects for the events the player has not heard yet, loudest first and without repeats. */
export function soundsFor(
  events: readonly { id: number; type: string; data: Record<string, unknown> }[],
  after: number,
  me: string,
): SfxKey[] {
  const out: SfxKey[] = [];
  for (const ev of events) {
    if (ev.id <= after) continue;
    // What the player said or pinged themself already made its sound under their finger.
    if ((ev.type === 'chat' || ev.type === 'ping') && ev.data['by'] === me) continue;
    for (const key of sfxForEvent(ev)) if (!out.includes(key)) out.push(key);
  }
  // Stingers first: they carry the news.
  out.sort((a, b) => Number(b.startsWith('stinger.')) - Number(a.startsWith('stinger.')));
  return out.slice(0, MAX_PER_UPDATE);
}

/**
 * Puts the sound of a game on: the music of the scene, its tempo and layers from the alarm, the
 * effects of what happens, and a tick in the last half minute. Used by the heist and by
 * Training, which both show the same views.
 */
export function useGameAudio(view: PlayerView, scene?: Scene): void {
  const phase = view.game.phase;
  const mapId = view.game.mapId;
  // The score is composed from a seed of its own: the game's seed never leaves the server.
  const [seed] = useState(() => `${mapId}/${Math.random().toString(36).slice(2, 10)}`);
  const seen = useRef(Math.max(0, ...view.events.map((e) => e.id)));
  const lastTick = useRef(-1);

  const target = scene ?? sceneForPhase(phase);
  useEffect(() => {
    audio.setSeed(seed);
    audio.setScene(target);
  }, [seed, target]);

  const alarm = (view.exec?.alarm ?? 0) / 1000;
  useEffect(() => {
    audio.setAlarm(alarm);
  }, [alarm]);
  useEffect(
    () => () => {
      audio.setAlarm(0);
    },
    [],
  );

  useEffect(() => {
    const keys = soundsFor(view.events, seen.current, view.you.id);
    const newest = view.events.at(-1)?.id;
    if (newest !== undefined) seen.current = Math.max(seen.current, newest);
    for (const key of keys) audio.sfx(key);
  }, [view.events, view.you.id]);

  const left = view.exec?.timerMs ?? null;
  useEffect(() => {
    if (left === null || left > 30_000 || left <= 0) return;
    const second = Math.ceil(left / 1000);
    if (second !== lastTick.current) {
      lastTick.current = second;
      audio.sfx('tick');
    }
  }, [left]);
}
